/**
 * @license
 * @copyright Copyright Motili Inc., 2023 All Rights Reserved
 */

import { ChangeEvent, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { CompanyInfo } from '../common/types';
import StyledTextInput from './styled/StyledTextInput';
import StateDropdown from './StateDropdown';

interface AddressFormProps {
    companyInfo: CompanyInfo;
    addressType: 'billingAddress' | 'companyAddress';
    handleCompanyInfoOnChange: (info: any) => void;
}

export default function AddressForm({
    companyInfo,
    addressType,
    handleCompanyInfoOnChange,
}: AddressFormProps): ReactElement {
    const {
        register,
        formState: { errors },
    } = useForm({ mode: 'onBlur' });

    const setCompanyAndBillingAddress = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const { target } = event;
        handleCompanyInfoOnChange({
            companyAddress: {
                ...companyInfo.companyAddress,
                [target.name]: target.value,
            },
            billingAddress: {
                ...companyInfo.billingAddress,
                [target.name]: target.value,
            },
        });
    };

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (companyInfo.useCompanyAddressAsBilling) {
            setCompanyAndBillingAddress(event);
        } else {
            handleCompanyInfoOnChange({
                [addressType]: {
                    ...companyInfo[addressType],
                    [event.target.name]: event.target.value,
                },
            });
        }
    };

    return (
        <div style={{ marginTop: '20px' }}>
            <StyledTextInput
                required
                name={'address1'}
                error={!!errors.address1}
                errorMessage={'Address cannot be blank'}
                register={register('address1', {
                    required: true,
                })}
                onChange={handleInputChange}
                value={companyInfo[addressType].address1}
                style={inputStyles}
                label={
                    addressType === 'billingAddress'
                        ? 'Billing Address'
                        : 'Company Address'
                }
                placeholder='Street Address'
            />
            <StyledTextInput
                name={'address2'}
                register={register('address2', {
                    required: false,
                })}
                onChange={handleInputChange}
                value={companyInfo[addressType].address2}
                style={inputStyles}
                label=''
                placeholder='Address Line 2'
            />
            <StyledTextInput
                name={'city'}
                register={register('city', {
                    required: true,
                })}
                error={!!errors.city}
                errorMessage={'City cannot be blank'}
                onChange={handleInputChange}
                value={companyInfo[addressType].city}
                style={inputStyles}
                label=''
                placeholder='City'
            />
            <StateDropdown
                companyInfo={companyInfo}
                addressType={addressType}
                handleCompanyInfoOnChange={handleCompanyInfoOnChange}
            />
            <StyledTextInput
                marginTop='10px'
                name={'zip'}
                register={register('zip', {
                    required: {
                        value: true,
                        message: 'Zip code cannot be blank',
                    },
                    pattern: {
                        value: /(^\d{5}$)/,
                        message: 'Zip code must be a 5 digit number',
                    },
                })}
                error={!!errors.zip}
                errorMessage={(errors.zip?.message as string) || ''}
                onChange={handleInputChange}
                value={companyInfo[addressType].zip}
                style={inputStyles}
                label=''
                maxLength={5}
                placeholder='Zip Code'
            />
        </div>
    );
}

const inputStyles = { width: '100%', margin: '10px 0' };
